import { createGlobalState, computedAsync, until } from '@vueuse/core'
import { useRouter, type RouteMap } from 'vue-router'
import { computed, ref } from 'vue'
import {
  CacheLookupPolicy,
  InteractionRequiredAuthError,
  LogLevel,
  createStandardPublicClientApplication
} from '@azure/msal-browser'

import { authConfig } from '@/opendoors-api/client/sdk.gen'
import { createClient } from '@/opendoors-api/useClient'
import { clearAllStores } from '@/store'
import { useAppStartup } from '@/composables/useAppStartup'

const loginPage: keyof RouteMap = 'LoginPage'
const authRedirectPage: keyof RouteMap = 'AuthRedirect'

export const useAuth = createGlobalState(() => {
  const router = useRouter()
  const { reportNetworkError } = useAppStartup()
  const entraAccount = ref<string | null>(null)
  const isAuthenticatedRef = ref(false)

  const apiConfig = computedAsync(
    async () => {
      const { data, error, response } = await authConfig({ client: createClient() })
      if (error) {
        if (reportNetworkError(error, response)) {
          return null
        }
        throw Error('Error getting auth configuration')
      }
      return data!.entraId
    },
    null,
    { lazy: true }
  )

  const getEntraClient = async () => {
    await until(apiConfig).toBeTruthy()
    const redirectURL = absoluteURL(router.resolve({ name: authRedirectPage }).href)
    const postLogoutURL = absoluteURL(router.resolve({ name: loginPage }).href)
    const msalConfig = {
      auth: {
        clientId: apiConfig.value!.clientId,
        authority: apiConfig.value!.authority,
        redirectUri: redirectURL.href,
        postLogoutUri: postLogoutURL.href,
        navigateToLoginRequestUrl: false
      },
      cache: {
        cacheLocation: 'sessionStorage'
      },
      system: {
        loggerOptions: loggerOptions
      }
    }
    return await createStandardPublicClientApplication(msalConfig)
  }

  const getAccessToken = async () => {
    await until(entraClient).toBeTruthy()
    if (entraClient.value) {
      const account = entraClient.value.getActiveAccount()
      try {
        if (account) {
          const resp = await entraClient.value.acquireTokenSilent({
            account: account,
            scopes: apiConfig.value!.scopes,
            cacheLookupPolicy: CacheLookupPolicy.Default
          })
          if (!entraAccount.value) {
            entraAccount.value = account.localAccountId
          }
          if (!isAuthenticatedRef.value) {
            isAuthenticatedRef.value = true
          }
          return resp.accessToken
        }
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          isAuthenticatedRef.value = false
          console.debug('interaction required')
          return null
        }
        console.error(err)
        throw err
      }
    }
    return null
  }

  const getAuthenticated = async () => {
    const isAuth = !!(await getAccessToken())
    return isAuth
  }

  const entraClient = computedAsync(
    async () => {
      const client = await getEntraClient()
      return client
    },
    null,
    { lazy: true }
  )

  const login = async () => {
    await until(entraClient).toBeTruthy()
    await entraClient.value!.loginRedirect({
      scopes: apiConfig.value!.scopes
    })
  }

  const logout = async () => {
    clearAllStores()
    await until(entraClient).toBeTruthy()
    const account = entraClient.value!.getActiveAccount()
    if (account) {
      const redirectURI = absoluteURL(router.resolve({ name: 'LoginPage' }).href)
      redirectURI.searchParams.set('loggedOut', '')
      await entraClient.value!.logoutRedirect({
        account: account,
        postLogoutRedirectUri: redirectURI.href
      })
    }
  }

  const handleRedirect = async () => {
    await until(entraClient).toBeTruthy()
    const result = await entraClient.value!.handleRedirectPromise()
    if (result) {
      entraClient.value!.setActiveAccount(result.account)
      entraAccount.value = result.account.localAccountId
      isAuthenticatedRef.value = true
      return true
    } else {
      entraAccount.value = null
      isAuthenticatedRef.value = false
    }
    return false
  }

  const isAuthenticated = computed(() => {
    return isAuthenticatedRef.value
  })

  return {
    entraClient,
    isAuthenticated,
    getAccessToken,
    getAuthenticated,
    handleRedirect,
    login,
    logout
  }
})

function absoluteURL(path: string) {
  return new URL(path, window.location.origin)
}

const loggerOptions = {
  logLevel: import.meta.env.VITE_DEBUG_ENTRA ? LogLevel.Verbose : LogLevel.Error,
  loggerCallback: (level: LogLevel, message: string, containsPii: boolean): void => {
    if (containsPii) {
      return
    }
    switch (level) {
      case LogLevel.Error:
        console.error(message)
        return
      case LogLevel.Info:
        console.info(message)
        return
      case LogLevel.Verbose:
        console.debug(message)
        return
      case LogLevel.Warning:
        console.warn(message)
        return
    }
  },
  piiLoggingEnabled: false
}
